import { addCalendarLabel } from 'owa-locstrings/lib/strings/addcalendarlabel.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import { LazyPublicFolderPicker } from 'owa-publicfolder-picker';
import { observer } from 'mobx-react';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { getFlexContainerStyle } from './getFlexContainerStyle';
import { getPrimaryAndConnectedAccountsEmailAddresses } from 'owa-session-store';
import CalendarAccountGroup from './CalendarAccountGroup';
import { initializeCalendarPickerStore } from '../mutators/initializeStoreMutator';
import type CalendarPickerScenarioId from '../store/schema/CalendarPickerScenarioId';
import {
    getOWAAccountProvider,
    getOWAConnectedAccounts,
    isMonarchMultipleAccountsEnabled,
} from 'owa-accounts-store';
import styles from './CalendarPicker.scss';
import {
    personalBookingHoverDescription,
    setupBookingButtonText,
    manageBookingButtonText,
} from './personalbookinglabel.locstring.json';
import { getBookingPageSetupStatus } from 'owa-pbp-option-publishing-settings';
import getAnonymousSharingStatus from 'owa-pbp-option-publishing-settings/lib/selectors/getAnonymousSharingStatus';
import getOWACalendarBookingButtonEnabled from 'owa-pbp-option-publishing-settings/lib/selectors/getOWACalendarBookingButtonEnabled';
import { isFeatureEnabled } from 'owa-feature-flags';
import CalendarPersonRegular from 'owa-fluent-icons-svg/lib/icons/CalendarPersonRegular';
import { addAccount } from '../strings.locstring.json';

/**
 * Calendar picker properties
 * itemClicked: click handler for calendar nodes
 */

export interface CalendarPickerProps {
    scenarioId: CalendarPickerScenarioId;
    className: string;
    showCalendarPicker: boolean;
    onClickAddCalendar?: () => void;
    onClickAddAccount?: () => void;
    onClickAccountSettings?: () => void;
    onClickAddTeamsCalendar?: () => void;
    onClickSetupBooking?: (alreadySetupBooking) => void;
}

/**
 * The state for the calendar picker
 * isCreatingCalendarGroup: whether new calendar group is being created
 */
export interface CalendarPickerGroupState {
    isCreatingCalendarGroup: boolean;
}

@observer
export default class CalendarPicker extends React.Component<CalendarPickerProps, {}> {
    constructor(props: CalendarPickerProps) {
        super(props);
        initializeCalendarPickerStore(props.scenarioId);
    }

    componentDidMount() {
        getBookingPageSetupStatus();
    }

    render() {
        const {
            showCalendarPicker,
            onClickAddCalendar,
            onClickAddAccount,
            onClickAddTeamsCalendar,
            onClickSetupBooking,
        } = this.props;
        const flexContainerStyle = getFlexContainerStyle();

        const shouldShowPersonalBookingsButton =
            isFeatureEnabled('eventify-booking-calendar-button') &&
            getOWACalendarBookingButtonEnabled();

        const shouldShowAddAccountButton = isMonarchMultipleAccountsEnabled();

        let accountsSmtpAddresses: string[] = [];
        if (isMonarchMultipleAccountsEnabled()) {
            accountsSmtpAddresses = getOWAConnectedAccounts().map(account => account.userIdentity);
        } else {
            accountsSmtpAddresses = getPrimaryAndConnectedAccountsEmailAddresses();
            const connectedAccounts = getOWAConnectedAccounts();

            /*
             * Check to ensure connected accounts in the owa-accounts-store were properly loaded into owa-session-store.
             * If they were not, we should add the user identity to the calendar picker so the user knows they are in an error
             * state and is given the option to reconnect their account.
             */

            connectedAccounts.forEach(connectedAccount => {
                const userIdentity = connectedAccount.userIdentity;
                if (!accountsSmtpAddresses.includes(userIdentity)) {
                    accountsSmtpAddresses.push(userIdentity);
                }
            });
        }

        const hasMultipleAccounts = accountsSmtpAddresses.length > 1;

        return (
            <div className={styles.calendarPickersAndAddCalendar}>
                <div className={styles.spacedPickerButton}>
                    <DefaultButton
                        className={styles.calendarListDefaultButton}
                        onClick={onClickAddCalendar}
                        title={loc(addCalendarLabel)}
                        styles={flexContainerStyle}>
                        <Icon className={styles.listButtonIcon} iconName={ControlIcons.AddEvent} />
                        <div className={styles.listItemLabel}>{loc(addCalendarLabel)}</div>
                    </DefaultButton>
                    {shouldShowPersonalBookingsButton && getOWACalendarBookingButtonEnabled() ? (
                        <DefaultButton
                            style={{ paddingLeft: '12.5px' }}
                            className={styles.calendarListDefaultButton}
                            onClick={onClickSetupBooking}
                            title={loc(personalBookingHoverDescription)}
                            styles={flexContainerStyle}>
                            <Icon
                                className={styles.listButtonIcon}
                                iconName={CalendarPersonRegular}
                                style={{ fontSize: '20px', paddingBottom: '17px' }}
                            />
                            <div className={styles.listItemLabel} style={{ paddingLeft: '2.5px' }}>
                                {getAnonymousSharingStatus()
                                    ? loc(manageBookingButtonText)
                                    : loc(setupBookingButtonText)}
                            </div>
                        </DefaultButton>
                    ) : null}
                </div>
                {showCalendarPicker && (
                    <>
                        <div className={styles.calendarPickers}>
                            {accountsSmtpAddresses.map(userIdentity => (
                                <CalendarAccountGroup
                                    key={userIdentity}
                                    scenarioId={this.props.scenarioId}
                                    userIdentity={userIdentity}
                                    accountProvider={getOWAAccountProvider(userIdentity)}
                                    hasMultipleAccounts={hasMultipleAccounts}
                                    className={this.props.className}
                                    onClickAccountSettings={this.props.onClickAccountSettings}
                                    onClickAddTeamsCalendar={onClickAddTeamsCalendar}
                                />
                            ))}
                        </div>
                        <LazyPublicFolderPicker />
                    </>
                )}
                {shouldShowAddAccountButton && (
                    <DefaultButton
                        className={styles.calendarListDefaultButton}
                        onClick={onClickAddAccount}
                        title={loc(addAccount)}
                        styles={flexContainerStyle}>
                        <Icon className={styles.listButtonIcon} iconName={ControlIcons.Add} />
                        <div className={styles.listItemLabel}>{loc(addAccount)}</div>
                    </DefaultButton>
                )}
            </div>
        );
    }
}
